import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ButtonGroup,
  TextField,
  Stack,
  Alert,
  Chip,
  Divider,
  MenuItem,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import type { ScheduleEntry } from '@dosfilos/domain';
import { TenantDateField } from '../../../components/TenantDateField';
import { LockClockRounded, LockOpenRounded, AutorenewRounded } from '@mui/icons-material';
import { useTenantStore, useTenantTimeZone } from '../../../stores/tenant.store';
import { todayInZoneWeb } from '@dosfilos/shared';
import { useAcademic } from '../../../hooks/use-academic';
import { definedCapacity } from '../../../lib/enrollment-count';
import { errorText } from '../../../lib/error-text';
import { SectionScheduleEditor, scheduleDraftProblem } from './SectionScheduleEditor';
import {
  deriveSectionLifecycle,
  formatDateEs,
  type SectionLifecycle,
} from '../../portal-v2/util/lifecycle';

interface EditSectionDialogProps {
  open: boolean;
  onClose: () => void;
  section: any | null;
  courses: any[];
  terms: any[];
  faculty: any[];
}

// «Notas cerradas» NO se elige aquí (H1): el acta se cierra y se reabre en el
// acta de la sección, con sus notas, su auditoría y —para reabrir— un motivo.
const STATUS_OPTIONS = ['open', 'closed', 'cancelled'] as const;

export function EditSectionDialog({
  open,
  onClose,
  section,
  courses,
  terms,
  faculty,
}: EditSectionDialogProps) {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const todayYmd = todayInZoneWeb(useTenantTimeZone());
  const { updateSection, isUpdatingSection, setSectionLifecycle, isSettingSectionLifecycle } =
    useAcademic();

  const [facultyId, setFacultyId] = useState('');
  const [assistantIds, setAssistantIds] = useState<string[]>([]);
  const [maxEnrollment, setMaxEnrollment] = useState<number | ''>('');
  // «Sin límite de cupo» = cupo 0 (H7). Una sección importada sin cupo abre
  // con el interruptor encendido.
  const [unlimited, setUnlimited] = useState(false);
  const [sectionCode, setSectionCode] = useState('');
  // null = horario SIN TOCAR: no se envía, y el servidor lo deja como está.
  const [schedule, setSchedule] = useState<ScheduleEntry[] | null>(null);
  const [status, setStatus] = useState('open');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [lifecycleMsg, setLifecycleMsg] = useState<string | null>(null);

  const [currentOverride, setCurrentOverride] = useState<'active' | 'concluded' | null>(null);

  // Populate fields when section changes
  useEffect(() => {
    if (section) {
      setFacultyId(section.facultyId ?? '');
      setAssistantIds((section.assistantIds as string[] | undefined) ?? []);
      // 0 = sin cupo definido (p. ej. una sección importada): «Sin límite».
      setMaxEnrollment(definedCapacity(section.maxEnrollment) ?? '');
      setUnlimited(definedCapacity(section.maxEnrollment) === null);
      setSectionCode((section.sectionCode as string | undefined) ?? '');
      setSchedule(null);
      setStatus(section.status ?? 'open');
      // Convert Firestore Timestamp or Date to 'YYYY-MM-DD' for the date input
      const toDateStr = (val: any): string => {
        if (!val) return '';
        // If already a plain YYYY-MM-DD string, return as-is
        if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
        const d = val.toDate ? val.toDate() : new Date(val);
        return d instanceof Date && !isNaN(d.getTime()) ? d.toISOString().substring(0, 10) : '';
      };
      setStartDate(toDateStr(section.startDate));
      setEndDate(toDateStr(section.endDate));
      setCurrentOverride(
        section.lifecycleOverride === 'active' || section.lifecycleOverride === 'concluded'
          ? section.lifecycleOverride
          : null,
      );
      setError(null);
      setLifecycleMsg(null);
    }
  }, [section]);

  const derivedLifecycle: SectionLifecycle = section
    ? deriveSectionLifecycle(
        {
          startDate: startDate || null,
          endDate: endDate || null,
          lifecycleOverride: currentOverride,
        },
        todayYmd,
      )
    : 'active';

  const lifecycleLabel: Record<SectionLifecycle, string> = {
    upcoming: t('sections.editDialog.lifecycle.upcoming'),
    active: t('sections.editDialog.lifecycle.active'),
    concluded: t('sections.editDialog.lifecycle.concluded'),
    archived: t('sections.editDialog.lifecycle.archived'),
  };
  const lifecycleColor: Record<SectionLifecycle, 'default' | 'success' | 'warning' | 'info'> = {
    upcoming: 'info',
    active: 'success',
    concluded: 'warning',
    archived: 'default',
  };

  const handleSetLifecycle = async (override: 'active' | 'concluded' | null) => {
    if (!currentTenantId || !section) return;
    setLifecycleMsg(null);
    try {
      await setSectionLifecycle({
        tenantId: currentTenantId,
        sectionId: section.id,
        override,
      });
      setCurrentOverride(override);
      setLifecycleMsg(
        override === 'concluded'
          ? t('sections.editDialog.lifecycle.msgConcluded')
          : override === 'active'
            ? t('sections.editDialog.lifecycle.msgActive')
            : t('sections.editDialog.lifecycle.msgCleared'),
      );
    } catch (err) {
      setError(errorText(err, t('sections.editDialog.lifecycle.error')));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentTenantId || !section) return;

    setError(null);
    try {
      const params: Parameters<typeof updateSection>[0] = {
        tenantId: currentTenantId,
        sectionId: section.id,
        status: status as any,
        startDate: startDate || null,
        endDate: endDate || null,
      };
      if (facultyId) params.facultyId = facultyId;
      // SIEMPRE SE MANDA, también vacía: omitirla significa «no tocar», y
      // entonces quitar al último ayudante no lo quitaría de nada.
      params.assistantIds = assistantIds.filter((a) => a !== facultyId);
      // El cupo se envía solo si se cambió. «Sin límite» viaja como 0.
      const hadCapacity = definedCapacity(section.maxEnrollment) !== null;
      if (unlimited) {
        if (hadCapacity) params.maxEnrollment = 0;
      } else if (maxEnrollment !== '' && Number(maxEnrollment) !== section.maxEnrollment) {
        params.maxEnrollment = Number(maxEnrollment);
      }
      const code = sectionCode.trim();
      if (code !== ((section.sectionCode as string | undefined) ?? '')) params.sectionCode = code;
      // Solo si se editó: un horario importado sin tocar no viaja.
      if (schedule !== null) params.schedule = schedule;

      await updateSection(params);
      onClose();
    } catch (err: any) {
      setError(errorText(err, t('sections.editDialog.saveError')));
    }
  };

  const courseName =
    courses.find((c) => c.id === section?.courseId)?.name ?? section?.courseId ?? '';
  const termName = terms.find((t) => t.id === section?.termId)?.name ?? section?.termId ?? '';
  const capacityValid = unlimited || (maxEnrollment !== '' && Number(maxEnrollment) >= 1);
  const scheduleValid = !schedule || schedule.length === 0 || !scheduleDraftProblem(schedule);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('sections.editDialog.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {/* Read-only context fields */}
            <TextField
              label={t('sections.editDialog.course')}
              value={courseName}
              disabled
              fullWidth
              size="small"
            />
            <TextField
              label={t('sections.editDialog.term')}
              value={termName}
              disabled
              fullWidth
              size="small"
            />

            {/* Editable fields */}
            <TextField
              select
              label={t('sections.editDialog.faculty')}
              fullWidth
              value={facultyId}
              onChange={(e) => setFacultyId(e.target.value)}
            >
              <MenuItem value="">{t('sections.editDialog.unassigned')}</MenuItem>
              {faculty.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.firstName} {f.lastName}
                </MenuItem>
              ))}
            </TextField>

            {/* AYUDANTES. Ven la sección como el titular —quién no entregó, qué
                falta corregir— sin ser responsables de ella. El titular queda
                fuera de la lista: ya enseña acá por ser titular, y estar en las
                dos haría que quitarlo de una no cambiara nada. */}
            <TextField
              select
              label={t('sections.editDialog.assistants')}
              fullWidth
              value={assistantIds}
              onChange={(e) =>
                setAssistantIds(
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',')
                    : (e.target.value as unknown as string[]),
                )
              }
              slotProps={{ select: { multiple: true } }}
              helperText={t('sections.editDialog.assistantsHelper')}
            >
              {faculty
                .filter((f) => f.id !== facultyId)
                .map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.firstName} {f.lastName}
                  </MenuItem>
                ))}
            </TextField>

            <TextField
              label={t('sections.createDialog.sectionCode')}
              fullWidth
              value={sectionCode}
              onChange={(e) => setSectionCode(e.target.value)}
              helperText={t('sections.createDialog.sectionCodeHelper')}
            />

            <Box>
              <FormControlLabel
                control={
                  <Switch checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)} />
                }
                label={t('sections.createDialog.unlimited')}
              />
              {unlimited && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('sections.createDialog.unlimitedHelper')}
                </Typography>
              )}
            </Box>
            {unlimited ? null : (
              <TextField
                label={t('sections.editDialog.maxEnrollment')}
                type="number"
                required
                fullWidth
                value={maxEnrollment}
                onChange={(e) => setMaxEnrollment(e.target.value ? Number(e.target.value) : '')}
                slotProps={{ htmlInput: { min: 1 } }}
                helperText={t('sections.editDialog.maxEnrollmentHelper')}
              />
            )}

            <SectionScheduleEditor
              original={(section?.schedule as ScheduleEntry[] | undefined) ?? []}
              value={schedule}
              onChange={setSchedule}
            />

            <TextField
              select
              label={t('sections.editStatus.label')}
              fullWidth
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={status === 'grades_closed'}
              helperText={
                status === 'grades_closed' ? t('sections.editStatus.gradesClosedHelp') : undefined
              }
            >
              {status === 'grades_closed' && (
                <MenuItem value="grades_closed">
                  {t('sections.statusLabels.grades_closed')}
                </MenuItem>
              )}
              {STATUS_OPTIONS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`sections.statusLabels.${value}`)}
                </MenuItem>
              ))}
            </TextField>

            {/* Section dates */}
            <Stack direction="row" spacing={2}>
              <TenantDateField
                label={t('sections.editDialog.startDate')}
                fullWidth
                value={startDate}
                onChange={setStartDate}
                helperText={t('sections.editDialog.startDateHelper')}
              />
              <TenantDateField
                label={t('sections.editDialog.endDate')}
                fullWidth
                value={endDate}
                onChange={setEndDate}
                helperText={
                  derivedLifecycle === 'concluded' || derivedLifecycle === 'archived'
                    ? t('sections.editDialog.endDateHelperExtend')
                    : t('sections.editDialog.endDateHelper')
                }
              />
            </Stack>

            {/* Lifecycle controls */}
            <Divider />
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2">
                  {t('sections.editDialog.lifecycle.title')}
                </Typography>
                <Chip
                  size="small"
                  label={lifecycleLabel[derivedLifecycle]}
                  color={lifecycleColor[derivedLifecycle]}
                  variant="outlined"
                />
                {currentOverride && (
                  <Chip
                    size="small"
                    label={
                      currentOverride === 'active'
                        ? t('sections.editDialog.lifecycle.overrideActive')
                        : t('sections.editDialog.lifecycle.overrideConcluded')
                    }
                    sx={{ bgcolor: '#fef3c7', color: '#92400e' }}
                  />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {endDate
                  ? t('sections.editDialog.lifecycle.endsOn', { date: formatDateEs(endDate) })
                  : t('sections.editDialog.lifecycle.noEnd')}{' '}
                {t('sections.editDialog.lifecycle.concludedNote')}
              </Typography>
              {lifecycleMsg && <Alert severity="success">{lifecycleMsg}</Alert>}
              <ButtonGroup size="small" variant="outlined" disabled={isSettingSectionLifecycle}>
                <Button
                  startIcon={<LockClockRounded />}
                  onClick={() => handleSetLifecycle('concluded')}
                  color="warning"
                  disabled={currentOverride === 'concluded'}
                >
                  {t('sections.editDialog.lifecycle.concludeNow')}
                </Button>
                <Button
                  startIcon={<LockOpenRounded />}
                  onClick={() => handleSetLifecycle('active')}
                  color="success"
                  disabled={currentOverride === 'active'}
                >
                  {t('sections.editDialog.lifecycle.reopen')}
                </Button>
                {currentOverride && (
                  <Button startIcon={<AutorenewRounded />} onClick={() => handleSetLifecycle(null)}>
                    {t('sections.editDialog.lifecycle.useDates')}
                  </Button>
                )}
              </ButtonGroup>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={isUpdatingSection}>
            {t('sections.editDialog.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isUpdatingSection || !capacityValid || !scheduleValid}
            sx={{ minWidth: 120 }}
          >
            {isUpdatingSection ? t('sections.editDialog.saving') : t('sections.editDialog.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
